/**
 * UserManagement — admin-only page for managing portal users.
 *
 * Layout:
 *   - Header with "Add user" button (right side)
 *   - Table of users: email, status, role chips, dashboard access, last
 *     modified, actions
 *   - Side drawer (right-slides-in) for adding new users
 *   - Side drawer (right-slides-in) for editing an existing user
 *
 * Roles surfaced as toggles:
 *   - Administrator (admins group)
 *   - AI Analyst   (ai_analyst group)
 * Every user is implicitly a viewer; we don't expose a toggle for it.
 *
 * Dashboard access (per-user allowlist):
 *   - "All dashboards" (the default) or a specific set of dashboards.
 *   - Administrators always see every dashboard by role, so the editor is
 *     shown read-only for them.
 *   - The list of choosable dashboards comes from /api/tableau/dashboards,
 *     which for an admin returns every configured dashboard.
 *
 * Invitations:
 *   - New users get an email with a temporary password by default. Turn off
 *     "Send the invitation email now" to create the account silently and send
 *     the invite later with the row's "Resend invite" action.
 *
 * Operations:
 *   - Add user        POST   /api/admin/users
 *   - Edit user       PATCH  /api/admin/users/{username}   (roles + dashboards)
 *   - Resend invite   POST   /api/admin/users/{username}/resend-invite
 *   - Disable user    POST   /api/admin/users/{username}/disable
 *   - Enable user     POST   /api/admin/users/{username}/enable
 *
 * Safety:
 *   - The server prevents admins from removing their own admin role and from
 *     disabling their own account; we surface the server error rather than
 *     pre-disabling buttons.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  LayoutGrid,
  Mail,
  Plus,
  ShieldCheck,
  Sparkles,
  UserCog,
  UserX,
  X,
} from "lucide-react";

import {
  createUser,
  disableUser,
  enableUser,
  listUsers,
  patchUser,
  resendInvite,
  statusLabel,
  type PatchUserRequest,
  type UserSummary,
} from "../lib/admin";
import { fetchTableauDashboards, type TableauDashboard } from "../lib/tableau";

type ToggleableGroup = "admins" | "ai_analyst";

// Folder display order/labels for grouping the dashboard picker. Mirrors the
// Dashboards route so the two views read consistently.
const FOLDERS: Array<{ key: string; label: string }> = [
  { key: "sales", label: "Sales" },
  { key: "call_center", label: "Call Center" },
  { key: "marketing", label: "Marketing" },
  { key: "owner", label: "Owner" },
  { key: "", label: "Other" },
];

export function UserManagement() {
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The catalogue of dashboards an admin can grant. Loaded once.
  const [dashboards, setDashboards] = useState<TableauDashboard[]>([]);
  const [dashboardsLoading, setDashboardsLoading] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [editUsername, setEditUsername] = useState<string | null>(null);

  // Inline operation feedback (resend, enable, disable). Keyed by username.
  const [rowBusy, setRowBusy] = useState<Record<string, string>>({});
  const [rowFlash, setRowFlash] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const data = await listUsers();
      setUsers(data.users);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    fetchTableauDashboards()
      .then((d) => setDashboards(d.dashboards))
      .catch(() => setDashboards([])) // non-fatal: editor shows an empty note
      .finally(() => setDashboardsLoading(false));
  }, []);

  const editingUser = useMemo(
    () => users.find((u) => u.username === editUsername) ?? null,
    [users, editUsername],
  );

  const dashboardName = useCallback(
    (id: string) => dashboards.find((d) => d.id === id)?.name ?? id,
    [dashboards],
  );

  // Per-row helpers
  async function handleResendInvite(u: UserSummary) {
    setRowBusy((b) => ({ ...b, [u.username]: "Sending invite…" }));
    try {
      await resendInvite(u.username);
      setRowFlash((f) => ({ ...f, [u.username]: "Invite sent" }));
      setTimeout(() => {
        setRowFlash((f) => {
          const next = { ...f };
          delete next[u.username];
          return next;
        });
      }, 3000);
    } catch (e) {
      setRowFlash((f) => ({
        ...f,
        [u.username]: `Send failed: ${e instanceof Error ? e.message : "Unknown"}`,
      }));
    } finally {
      setRowBusy((b) => {
        const next = { ...b };
        delete next[u.username];
        return next;
      });
    }
  }

  async function handleDisable(u: UserSummary) {
    if (
      !window.confirm(
        `Disable ${u.email}? They will be unable to sign in. You can re-enable them later.`,
      )
    ) {
      return;
    }
    setRowBusy((b) => ({ ...b, [u.username]: "Disabling…" }));
    try {
      await disableUser(u.username);
      await refresh();
    } catch (e) {
      setRowFlash((f) => ({
        ...f,
        [u.username]: `Disable failed: ${e instanceof Error ? e.message : "Unknown"}`,
      }));
    } finally {
      setRowBusy((b) => {
        const next = { ...b };
        delete next[u.username];
        return next;
      });
    }
  }

  async function handleEnable(u: UserSummary) {
    setRowBusy((b) => ({ ...b, [u.username]: "Enabling…" }));
    try {
      await enableUser(u.username);
      await refresh();
    } catch (e) {
      setRowFlash((f) => ({
        ...f,
        [u.username]: `Enable failed: ${e instanceof Error ? e.message : "Unknown"}`,
      }));
    } finally {
      setRowBusy((b) => {
        const next = { ...b };
        delete next[u.username];
        return next;
      });
    }
  }

  return (
    // The app shell's <main> is h-screen overflow-hidden, so each route
    // provides its own scroll container (same pattern as Dashboards).
    <div className="p-8 view-in overflow-y-auto h-full">
      <header className="mb-6 flex items-end justify-between">
        <div>
          <h1
            className="font-serif text-[28px] leading-tight"
            style={{ color: "var(--ink)" }}
          >
            User Management
          </h1>
          <p
            className="text-[14.5px] font-serif italic mt-1"
            style={{ color: "var(--ink-3)" }}
          >
            Manage portal users, roles, and dashboard access.
          </p>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-serif text-[14px] t-base focus-ring"
          style={{
            background: "var(--ink)",
            color: "var(--bg)",
          }}
        >
          <Plus size={14} strokeWidth={2} />
          Add user
        </button>
      </header>

      {error && (
        <div
          className="rounded-md p-4 mb-4 font-mono text-[13px] flex items-start gap-2"
          style={{
            background: "var(--oxblood-bg)",
            color: "var(--oxblood)",
            border: "1px solid var(--oxblood)",
          }}
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>Failed to load users: {error}</span>
        </div>
      )}

      <section
        className="rounded-lg border hairline overflow-hidden"
        style={{ background: "var(--bg-elev)" }}
      >
        {loading ? (
          <div
            className="p-8 font-serif italic text-[14px]"
            style={{ color: "var(--ink-3)" }}
          >
            Loading users…
          </div>
        ) : users.length === 0 ? (
          <div
            className="p-8 font-serif italic text-[14px]"
            style={{ color: "var(--ink-3)" }}
          >
            No users yet. Click “Add user” to create the first one.
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr
                className="text-[10.5px] font-mono uppercase tracking-[0.14em] border-b hairline"
                style={{ color: "var(--ink-3)" }}
              >
                <th className="px-5 py-3 font-normal">Email</th>
                <th className="px-3 py-3 font-normal">Status</th>
                <th className="px-3 py-3 font-normal">Roles</th>
                <th className="px-3 py-3 font-normal">Dashboards</th>
                <th className="px-3 py-3 font-normal">Last modified</th>
                <th className="px-3 py-3 font-normal w-[1%]"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.username}
                  className="border-b hairline last:border-0 t-base"
                  style={{ opacity: u.enabled ? 1 : 0.55 }}
                >
                  <td
                    className="px-5 py-3 font-mono text-[13px]"
                    style={{ color: "var(--ink)" }}
                  >
                    {u.email}
                    {rowFlash[u.username] && (
                      <div
                        className="text-[10.5px] mt-1"
                        style={{ color: "var(--ink-3)" }}
                      >
                        {rowFlash[u.username]}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <StatusPill enabled={u.enabled} status={u.status} />
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1.5 flex-wrap">
                      {u.is_admin && <RolePill icon={ShieldCheck} label="Admin" />}
                      {u.has_ai_analyst && (
                        <RolePill icon={Sparkles} label="AI Analyst" />
                      )}
                      {!u.is_admin && !u.has_ai_analyst && (
                        <span
                          className="text-[12px] font-serif italic"
                          style={{ color: "var(--ink-4)" }}
                        >
                          Viewer
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <AccessCell user={u} />
                  </td>
                  <td
                    className="px-3 py-3 font-mono text-[12px]"
                    style={{ color: "var(--ink-3)" }}
                  >
                    {u.last_modified_at
                      ? new Date(u.last_modified_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1 justify-end">
                      <RowAction
                        label="Edit roles & access"
                        Icon={UserCog}
                        onClick={() => setEditUsername(u.username)}
                        busy={rowBusy[u.username]}
                      />
                      {u.status === "FORCE_CHANGE_PASSWORD" && (
                        <RowAction
                          label="Send invite"
                          Icon={Mail}
                          onClick={() => handleResendInvite(u)}
                          busy={rowBusy[u.username]}
                        />
                      )}
                      {u.enabled ? (
                        <RowAction
                          label="Disable"
                          Icon={UserX}
                          onClick={() => handleDisable(u)}
                          busy={rowBusy[u.username]}
                          dangerous
                        />
                      ) : (
                        <RowAction
                          label="Enable"
                          Icon={CheckCircle2}
                          onClick={() => handleEnable(u)}
                          busy={rowBusy[u.username]}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {addOpen && (
        <AddUserDrawer
          dashboards={dashboards}
          dashboardsLoading={dashboardsLoading}
          onClose={() => setAddOpen(false)}
          onCreated={async () => {
            setAddOpen(false);
            await refresh();
          }}
        />
      )}

      {editingUser && (
        <EditUserDrawer
          user={editingUser}
          dashboards={dashboards}
          dashboardsLoading={dashboardsLoading}
          dashboardName={dashboardName}
          onClose={() => setEditUsername(null)}
          onSaved={async () => {
            setEditUsername(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

// ---- Subcomponents ----

function AccessCell({ user }: { user: UserSummary }) {
  if (user.is_admin || user.all_dashboards) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[12px] font-serif"
        style={{ color: "var(--ink-3)" }}
      >
        <LayoutGrid size={12} strokeWidth={1.5} />
        All
      </span>
    );
  }
  const n = user.dashboards.length;
  return (
    <span
      className="inline-flex items-center gap-1 text-[12px] font-serif"
      style={{ color: n === 0 ? "var(--oxblood)" : "var(--ink-2)" }}
    >
      <LayoutGrid size={12} strokeWidth={1.5} />
      {n} {n === 1 ? "dashboard" : "dashboards"}
    </span>
  );
}

function StatusPill({ enabled, status }: { enabled: boolean; status: string }) {
  if (!enabled) {
    return (
      <span
        className="inline-block px-2 py-0.5 rounded text-[10.5px] font-mono uppercase tracking-[0.10em]"
        style={{ background: "var(--bg)", color: "var(--ink-4)", border: "1px solid var(--line)" }}
      >
        Disabled
      </span>
    );
  }

  const palette = {
    CONFIRMED: { bg: "var(--accent-soft)", fg: "var(--accent-ink)" },
    FORCE_CHANGE_PASSWORD: { bg: "var(--brass-bg)", fg: "var(--brass)" },
  }[status] || { bg: "var(--bg)", fg: "var(--ink-3)" };

  return (
    <span
      className="inline-block px-2 py-0.5 rounded text-[10.5px] font-mono uppercase tracking-[0.10em]"
      style={{ background: palette.bg, color: palette.fg }}
    >
      {statusLabel(status)}
    </span>
  );
}

function RolePill({ icon: Icon, label }: { icon: typeof ShieldCheck; label: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono uppercase tracking-[0.08em]"
      style={{ background: "var(--accent-soft)", color: "var(--accent-ink)" }}
    >
      <Icon size={11} strokeWidth={1.5} />
      {label}
    </span>
  );
}

function RowAction({
  label,
  Icon,
  onClick,
  busy,
  dangerous,
}: {
  label: string;
  Icon: typeof UserCog;
  onClick: () => void;
  busy?: string;
  dangerous?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={!!busy}
      title={label}
      className="inline-flex items-center gap-1 p-1.5 rounded hover:bg-[var(--hover)] t-base focus-ring disabled:opacity-50"
      style={{ color: dangerous ? "var(--oxblood)" : "var(--ink-2)" }}
    >
      <Icon size={14} strokeWidth={1.5} />
    </button>
  );
}

// ---- Drawers ----

function Drawer({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <>
      <div
        className="fixed inset-0 z-40"
        style={{ background: "rgba(30, 35, 42, 0.35)" }}
        onClick={onClose}
      />
      <aside
        className="fixed top-0 right-0 h-screen w-[460px] z-50 border-l hairline flex flex-col view-in"
        style={{ background: "var(--bg-elev)" }}
      >
        <header className="px-6 py-4 border-b hairline flex items-center justify-between shrink-0">
          <h2 className="font-serif text-[20px]" style={{ color: "var(--ink)" }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-[var(--hover)] t-base focus-ring"
            style={{ color: "var(--ink-3)" }}
            aria-label="Close"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </aside>
    </>
  );
}

function AddUserDrawer({
  dashboards,
  dashboardsLoading,
  onClose,
  onCreated,
}: {
  dashboards: TableauDashboard[];
  dashboardsLoading: boolean;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [groups, setGroups] = useState<Set<ToggleableGroup>>(new Set());
  const [sendInvite, setSendInvite] = useState(true);

  // Dashboard access. allMode=true means "all dashboards".
  const [allMode, setAllMode] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggleGroup(g: ToggleableGroup) {
    setGroups((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  }

  const isAdmin = groups.has("admins");
  // A restricted selection with nothing chosen would be ambiguous, so block it.
  const accessIncomplete = !isAdmin && !allMode && selected.size === 0;
  const canSubmit = !!email.trim() && !accessIncomplete && !submitting;

  async function submit() {
    setErr(null);
    setSubmitting(true);
    try {
      await createUser({
        email: email.trim(),
        given_name: givenName.trim() || undefined,
        family_name: familyName.trim() || undefined,
        groups: Array.from(groups),
        // Admins are unrestricted by role; send "*" so the record is explicit.
        dashboards: isAdmin || allMode ? ["*"] : Array.from(selected),
        suppress_invite: !sendInvite,
      });
      await onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer title="Add user" onClose={onClose}>
      <div className="space-y-5">
        <p className="text-[13px] font-serif" style={{ color: "var(--ink-2)" }}>
          {sendInvite
            ? "A welcome email with a temporary password will be sent to this address. They'll set a new password on first sign-in."
            : "The account will be created without an email. Use “Send invite” on the user's row when you're ready to bring them on."}
        </p>

        <Field label="Email">
          <input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            placeholder="someone@example.com"
            className="w-full px-3 py-2 rounded border hairline font-mono text-[13px] focus-ring"
            style={{ background: "var(--bg)", color: "var(--ink)" }}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="First name (optional)">
            <input
              type="text"
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
              className="w-full px-3 py-2 rounded border hairline text-[14px] focus-ring"
              style={{ background: "var(--bg)", color: "var(--ink)" }}
            />
          </Field>
          <Field label="Last name (optional)">
            <input
              type="text"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              className="w-full px-3 py-2 rounded border hairline text-[14px] focus-ring"
              style={{ background: "var(--bg)", color: "var(--ink)" }}
            />
          </Field>
        </div>

        <div>
          <SectionLabel>Roles</SectionLabel>
          <GroupToggle
            label="Administrator"
            description="Manage portal users and see all settings."
            checked={groups.has("admins")}
            onChange={() => toggleGroup("admins")}
          />
          <GroupToggle
            label="AI Analyst"
            description="Use the AI Analyst chat feature."
            checked={groups.has("ai_analyst")}
            onChange={() => toggleGroup("ai_analyst")}
          />
          <p className="text-[11px] font-serif italic mt-2" style={{ color: "var(--ink-4)" }}>
            All users have viewer access (dashboards, data entry) automatically.
          </p>
        </div>

        <DashboardAccessEditor
          dashboards={dashboards}
          loading={dashboardsLoading}
          allMode={allMode}
          setAllMode={setAllMode}
          selected={selected}
          setSelected={setSelected}
          disabled={isAdmin}
          disabledNote="Administrators can see every dashboard by role."
        />

        <div>
          <SectionLabel>Invitation</SectionLabel>
          <GroupToggle
            label="Send the invitation email now"
            description="Off = create the account silently and send the invite later."
            checked={sendInvite}
            onChange={() => setSendInvite((v) => !v)}
          />
        </div>

        {err && <DrawerError message={err} />}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded font-serif text-[14px] t-base hover:bg-[var(--hover)] focus-ring"
            style={{ color: "var(--ink-2)" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="px-4 py-2 rounded font-serif text-[14px] t-base focus-ring disabled:opacity-50"
            style={{ background: "var(--ink)", color: "var(--bg)" }}
          >
            {submitting ? "Creating…" : sendInvite ? "Create & send invite" : "Create user"}
          </button>
        </div>
      </div>
    </Drawer>
  );
}

function EditUserDrawer({
  user,
  dashboards,
  dashboardsLoading,
  dashboardName,
  onClose,
  onSaved,
}: {
  user: UserSummary;
  dashboards: TableauDashboard[];
  dashboardsLoading: boolean;
  dashboardName: (id: string) => string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const initialGroups = useMemo<Set<ToggleableGroup>>(() => {
    const s = new Set<ToggleableGroup>();
    if (user.is_admin) s.add("admins");
    if (user.has_ai_analyst) s.add("ai_analyst");
    return s;
  }, [user]);

  const [groups, setGroups] = useState<Set<ToggleableGroup>>(initialGroups);

  const initialAllMode = user.all_dashboards;
  const initialSelected = useMemo(() => new Set(user.dashboards), [user]);
  const [allMode, setAllMode] = useState(initialAllMode);
  const [selected, setSelected] = useState<Set<string>>(initialSelected);

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggleGroup(g: ToggleableGroup) {
    setGroups((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  }

  const isAdmin = groups.has("admins");

  const groupsDirty = useMemo(() => {
    if (groups.size !== initialGroups.size) return true;
    for (const g of groups) if (!initialGroups.has(g)) return true;
    return false;
  }, [groups, initialGroups]);

  const accessDirty = useMemo(() => {
    if (allMode !== initialAllMode) return true;
    if (allMode) return false; // both "all": no list to compare
    if (selected.size !== initialSelected.size) return true;
    for (const id of selected) if (!initialSelected.has(id)) return true;
    return false;
  }, [allMode, initialAllMode, selected, initialSelected]);

  const accessIncomplete = !isAdmin && !allMode && selected.size === 0;
  const dirty = groupsDirty || accessDirty;
  const canSave = dirty && !accessIncomplete && !saving;

  async function save() {
    setErr(null);
    setSaving(true);
    try {
      const body: PatchUserRequest = { groups: Array.from(groups) };
      if (accessDirty || groups.has("admins") !== user.is_admin) {
        body.dashboards = isAdmin || allMode ? ["*"] : Array.from(selected);
      }
      await patchUser(user.username, body);
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer title={user.email} onClose={onClose}>
      <div className="space-y-5">
        <dl className="grid grid-cols-[120px_1fr] gap-y-2 gap-x-4 text-[13px]">
          <dt className="font-mono uppercase text-[10.5px] tracking-[0.14em]" style={{ color: "var(--ink-3)" }}>
            Status
          </dt>
          <dd style={{ color: "var(--ink)" }}>
            {user.enabled ? statusLabel(user.status) : "Disabled"}
          </dd>
          <dt className="font-mono uppercase text-[10.5px] tracking-[0.14em]" style={{ color: "var(--ink-3)" }}>
            Created
          </dt>
          <dd className="font-mono" style={{ color: "var(--ink)" }}>
            {user.created_at ? new Date(user.created_at).toLocaleString() : "—"}
          </dd>
          <dt className="font-mono uppercase text-[10.5px] tracking-[0.14em]" style={{ color: "var(--ink-3)" }}>
            Groups
          </dt>
          <dd className="font-mono text-[12px]" style={{ color: "var(--ink-2)" }}>
            {user.groups.join(", ") || "(none)"}
          </dd>
        </dl>

        <div>
          <SectionLabel>Roles</SectionLabel>
          <GroupToggle
            label="Administrator"
            description="Manage portal users and see all settings."
            checked={groups.has("admins")}
            onChange={() => toggleGroup("admins")}
          />
          <GroupToggle
            label="AI Analyst"
            description="Use the AI Analyst chat feature."
            checked={groups.has("ai_analyst")}
            onChange={() => toggleGroup("ai_analyst")}
          />
        </div>

        <DashboardAccessEditor
          dashboards={dashboards}
          loading={dashboardsLoading}
          allMode={allMode}
          setAllMode={setAllMode}
          selected={selected}
          setSelected={setSelected}
          disabled={isAdmin}
          disabledNote="Administrators can see every dashboard by role."
          nameFor={dashboardName}
        />

        {err && <DrawerError message={err} />}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded font-serif text-[14px] t-base hover:bg-[var(--hover)] focus-ring"
            style={{ color: "var(--ink-2)" }}
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={!canSave}
            className="px-4 py-2 rounded font-serif text-[14px] t-base focus-ring disabled:opacity-50"
            style={{ background: "var(--ink)", color: "var(--bg)" }}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </Drawer>
  );
}

/**
 * DashboardAccessEditor — "All dashboards" vs a specific set, grouped by
 * folder. Shown read-only (disabled) for administrators, who see everything
 * by role.
 */
function DashboardAccessEditor({
  dashboards,
  loading,
  allMode,
  setAllMode,
  selected,
  setSelected,
  disabled,
  disabledNote,
  nameFor,
}: {
  dashboards: TableauDashboard[];
  loading: boolean;
  allMode: boolean;
  setAllMode: (v: boolean) => void;
  selected: Set<string>;
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>;
  disabled?: boolean;
  disabledNote?: string;
  nameFor?: (id: string) => string;
}) {
  const grouped = useMemo(() => {
    const known = new Set(FOLDERS.map((f) => f.key));
    const buckets = new Map<string, TableauDashboard[]>();
    for (const d of dashboards) {
      const key = d.folder && known.has(d.folder) ? d.folder : "";
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(d);
    }
    return FOLDERS.map((f) => ({ ...f, items: buckets.get(f.key) ?? [] })).filter(
      (f) => f.items.length > 0,
    );
  }, [dashboards]);

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Any dashboard ids the user already has that are no longer in the catalogue
  // (e.g. a dashboard removed from client.yaml). Surface them so they aren't
  // silently dropped on save.
  const orphanIds = useMemo(() => {
    const known = new Set(dashboards.map((d) => d.id));
    return Array.from(selected).filter((id) => !known.has(id));
  }, [dashboards, selected]);

  return (
    <div>
      <SectionLabel>Dashboard access</SectionLabel>

      {disabled ? (
        <p className="text-[12px] font-serif italic" style={{ color: "var(--ink-4)" }}>
          {disabledNote}
        </p>
      ) : (
        <>
          <div className="flex gap-2 mb-3">
            <ModeChip label="All dashboards" active={allMode} onClick={() => setAllMode(true)} />
            <ModeChip label="Specific dashboards" active={!allMode} onClick={() => setAllMode(false)} />
          </div>

          {!allMode &&
            (loading ? (
              <p className="text-[12px] font-serif italic" style={{ color: "var(--ink-3)" }}>
                Loading dashboards…
              </p>
            ) : dashboards.length === 0 ? (
              <p className="text-[12px] font-serif italic" style={{ color: "var(--ink-3)" }}>
                No dashboards are configured yet.
              </p>
            ) : (
              <div className="space-y-4">
                {grouped.map((folder) => (
                  <div key={folder.key || "other"}>
                    <div
                      className="text-[10px] font-mono uppercase tracking-[0.14em] mb-1.5"
                      style={{ color: "var(--ink-4)" }}
                    >
                      {folder.label}
                    </div>
                    <div className="space-y-1.5">
                      {folder.items.map((d) => (
                        <CheckRow
                          key={d.id}
                          label={d.name}
                          checked={selected.has(d.id)}
                          onChange={() => toggleOne(d.id)}
                        />
                      ))}
                    </div>
                  </div>
                ))}

                {orphanIds.length > 0 && (
                  <div>
                    <div
                      className="text-[10px] font-mono uppercase tracking-[0.14em] mb-1.5"
                      style={{ color: "var(--brass)" }}
                    >
                      Not in current config
                    </div>
                    <div className="space-y-1.5">
                      {orphanIds.map((id) => (
                        <CheckRow
                          key={id}
                          label={nameFor ? nameFor(id) : id}
                          checked={selected.has(id)}
                          onChange={() => toggleOne(id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {selected.size === 0 && (
                  <p className="text-[11px] font-serif italic" style={{ color: "var(--oxblood)" }}>
                    Select at least one dashboard, or switch to “All dashboards”.
                  </p>
                )}
              </div>
            ))}
        </>
      )}
    </div>
  );
}

function ModeChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1.5 rounded border hairline text-[12px] font-mono uppercase tracking-[0.08em] t-base focus-ring"
      style={{
        background: active ? "var(--accent-soft)" : "var(--bg)",
        color: active ? "var(--accent-ink)" : "var(--ink-3)",
        borderColor: active ? "var(--accent)" : "var(--line)",
      }}
    >
      {label}
    </button>
  );
}

function CheckRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      className="w-full text-left flex items-center gap-2.5 px-2.5 py-1.5 rounded border hairline t-base focus-ring"
      style={{
        background: checked ? "var(--accent-soft)" : "var(--bg)",
        borderColor: checked ? "var(--accent)" : "var(--line)",
      }}
    >
      <span
        className="h-4 w-4 rounded shrink-0 flex items-center justify-center"
        style={{
          background: checked ? "var(--accent)" : "var(--bg)",
          border: `1px solid ${checked ? "var(--accent)" : "var(--line)"}`,
        }}
      >
        {checked && <CheckCircle2 size={10} strokeWidth={2.5} color="white" />}
      </span>
      <span className="text-[13px] font-serif" style={{ color: "var(--ink)" }}>
        {label}
      </span>
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="text-[10.5px] font-mono uppercase tracking-[0.14em] mb-2"
      style={{ color: "var(--ink-3)" }}
    >
      {children}
    </div>
  );
}

function DrawerError({ message }: { message: string }) {
  return (
    <div
      className="rounded p-3 text-[12px] font-mono flex items-start gap-2"
      style={{
        background: "var(--oxblood-bg)",
        color: "var(--oxblood)",
        border: "1px solid var(--oxblood)",
      }}
    >
      <AlertCircle size={12} className="mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div
        className="text-[10.5px] font-mono uppercase tracking-[0.14em] mb-1"
        style={{ color: "var(--ink-3)" }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

function GroupToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      className="w-full text-left flex items-start gap-3 p-3 rounded border hairline t-base mb-2 focus-ring"
      style={{
        background: checked ? "var(--accent-soft)" : "var(--bg)",
        borderColor: checked ? "var(--accent)" : "var(--line)",
      }}
    >
      <span
        className="mt-0.5 h-4 w-4 rounded shrink-0 flex items-center justify-center"
        style={{
          background: checked ? "var(--accent)" : "var(--bg)",
          border: `1px solid ${checked ? "var(--accent)" : "var(--line)"}`,
        }}
      >
        {checked && <CheckCircle2 size={10} strokeWidth={2.5} color="white" />}
      </span>
      <span className="flex-1">
        <span className="block font-serif text-[14px]" style={{ color: "var(--ink)" }}>
          {label}
        </span>
        <span className="block text-[12px] font-serif" style={{ color: "var(--ink-3)" }}>
          {description}
        </span>
      </span>
    </button>
  );
}
